import React, { useState, useMemo } from "react";
import { Users, ChevronDown, ChevronUp, Search, Filter, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Mapping des positions pour regroupement
const POSITION_GROUPS = {
  "Gardien": ["GK", "Goalkeeper", "Gardien", "G"],
  "Défenseur": ["CB", "LB", "RB", "LWB", "RWB", "DC", "DG", "DD", "Défenseur", "Defense", "Defender"],
  "Milieu": ["CM", "CDM", "CAM", "LM", "RM", "MC", "MO", "MD", "Milieu", "Midfield", "Midfielder"],
  "Attaquant": ["ST", "CF", "LW", "RW", "SS", "AT", "AG", "AD", "Attaquant", "Attack", "Forward", "Striker"],
};

const getPositionGroup = (position) => {
  if (!position) return "Autre";
  const posUpper = position.toUpperCase();
  for (const [group, positions] of Object.entries(POSITION_GROUPS)) {
    if (positions.some(p => posUpper.includes(p.toUpperCase()))) {
      return group;
    }
  }
  return "Autre";
};

const getPositionColor = (group, isDarkMode) => {
  const colors = {
    "Gardien": isDarkMode ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-100 text-amber-700 border-amber-300",
    "Défenseur": isDarkMode ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-blue-100 text-blue-700 border-blue-300",
    "Milieu": isDarkMode ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-300",
    "Attaquant": isDarkMode ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-rose-100 text-rose-700 border-rose-300",
    "Autre": isDarkMode ? "bg-gray-500/20 text-gray-300 border-gray-500/30" : "bg-gray-100 text-gray-700 border-gray-300",
  };
  return colors[group] || colors["Autre"];
};

const PlayerAssignmentSection = ({
  isDarkMode,
  players,
  assignedPlayers,
  setAssignedPlayers,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPositionFilter, setSelectedPositionFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const getPlayerName = (p) =>
    p.name ||
    p.full_name ||
    `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() ||
    p.id;

  // Grouper les joueurs par position
  const playersByPosition = useMemo(() => {
    const groups = {};
    for (const player of players) {
      const group = getPositionGroup(player.position);
      if (!groups[group]) groups[group] = [];
      groups[group].push(player);
    }
    return groups;
  }, [players]);

  // Positions disponibles (avec compte)
  const availablePositions = useMemo(() => {
    return Object.entries(playersByPosition)
      .map(([group, players]) => ({ group, count: players.length }))
      .sort((a, b) => {
        const order = ["Gardien", "Défenseur", "Milieu", "Attaquant", "Autre"];
        return order.indexOf(a.group) - order.indexOf(b.group);
      });
  }, [playersByPosition]);

  // Filtrer les joueurs
  const filteredPlayers = useMemo(() => {
    let result = players;

    // Filtre par position
    if (selectedPositionFilter) {
      result = result.filter(p => getPositionGroup(p.position) === selectedPositionFilter);
    }

    // Filtre par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => {
        const name = getPlayerName(p).toLowerCase();
        const position = (p.position || "").toLowerCase();
        const number = String(p.number || p.jersey_number || "");
        return name.includes(query) || position.includes(query) || number.includes(query);
      });
    }

    // Trier par position puis par nom
    return result.sort((a, b) => {
      const groupA = getPositionGroup(a.position);
      const groupB = getPositionGroup(b.position);
      const order = ["Gardien", "Défenseur", "Milieu", "Attaquant", "Autre"];
      const orderDiff = order.indexOf(groupA) - order.indexOf(groupB);
      if (orderDiff !== 0) return orderDiff;
      return getPlayerName(a).localeCompare(getPlayerName(b));
    });
  }, [players, selectedPositionFilter, searchQuery]);

  const handleTogglePlayer = (playerId) => {
    const newList = assignedPlayers.includes(playerId)
      ? assignedPlayers.filter((id) => id !== playerId)
      : [...assignedPlayers, playerId];
    setAssignedPlayers(newList);
  };

  const handleSelectAll = () => {
    // Sélectionner tous les joueurs filtrés
    const idsToAdd = filteredPlayers.map(p => p.id);
    const newList = [...new Set([...assignedPlayers, ...idsToAdd])];
    setAssignedPlayers(newList);
  };

  const handleDeselectAll = () => {
    // Désélectionner tous les joueurs filtrés
    const idsToRemove = new Set(filteredPlayers.map(p => p.id));
    const newList = assignedPlayers.filter(id => !idsToRemove.has(id));
    setAssignedPlayers(newList);
  };

  const handleSelectByPosition = (positionGroup) => {
    const playersInGroup = players.filter(p => getPositionGroup(p.position) === positionGroup);
    const idsToAdd = playersInGroup.map(p => p.id);
    const newList = [...new Set([...assignedPlayers, ...idsToAdd])];
    setAssignedPlayers(newList);
  };

  const handleDeselectByPosition = (positionGroup) => {
    const playersInGroup = players.filter(p => getPositionGroup(p.position) === positionGroup);
    const idsToRemove = new Set(playersInGroup.map(p => p.id));
    const newList = assignedPlayers.filter(id => !idsToRemove.has(id));
    setAssignedPlayers(newList);
  };

  // Compter les joueurs sélectionnés par position
  const selectedCountByPosition = useMemo(() => {
    const counts = {};
    for (const playerId of assignedPlayers) {
      const player = players.find(p => p.id === playerId);
      if (player) {
        const group = getPositionGroup(player.position);
        counts[group] = (counts[group] || 0) + 1;
      }
    }
    return counts;
  }, [assignedPlayers, players]);

  return (
    <div className="mb-4">
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600 hover:bg-gray-700"
            : "bg-gray-100 border-gray-300 hover:bg-gray-200"
        }`}
      >
        <div className="flex items-center gap-2">
          <Users size={18} className="text-sky-500" />
          <span className="font-medium">{t("calendar_ui.assigned_players")}</span>
          <span className={`text-sm px-2 py-0.5 rounded-full ${
            isDarkMode ? "bg-sky-500/20 text-sky-300" : "bg-sky-100 text-sky-700"
          }`}>
            {assignedPlayers.length} / {players.length}
          </span>
        </div>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {isExpanded && (
        <div className={`mt-2 p-3 rounded-lg border ${
          isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
        }`}>
          {/* Search Bar */}
          <div className="flex gap-2 mb-3">
            <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border ${
              isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}>
              <Search size={16} className="opacity-50" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("calendar_ui.search_player")}
                className={`flex-1 bg-transparent outline-none text-sm ${
                  isDarkMode ? "placeholder-gray-500" : "placeholder-gray-400"
                }`}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="opacity-50 hover:opacity-100">
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg border transition flex items-center gap-1 ${
                showFilters || selectedPositionFilter
                  ? isDarkMode
                    ? "bg-sky-500/20 border-sky-500/50 text-sky-300"
                    : "bg-sky-100 border-sky-300 text-sky-700"
                  : isDarkMode
                  ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <Filter size={16} />
            </button>
          </div>

          {/* Position Filters */}
          {showFilters && (
            <div className="mb-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPositionFilter(null)}
                  className={`px-2 py-1 text-xs rounded-full border transition ${
                    !selectedPositionFilter
                      ? isDarkMode
                        ? "bg-sky-500/30 border-sky-500 text-sky-200"
                        : "bg-sky-200 border-sky-400 text-sky-800"
                      : isDarkMode
                      ? "bg-gray-800 border-gray-600 hover:bg-gray-700"
                      : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                  }`}
                >
                  Tous ({players.length})
                </button>
                {availablePositions.map(({ group, count }) => (
                  <button
                    key={group}
                    type="button"
                    onClick={() => setSelectedPositionFilter(
                      selectedPositionFilter === group ? null : group
                    )}
                    className={`px-2 py-1 text-xs rounded-full border transition ${
                      selectedPositionFilter === group
                        ? getPositionColor(group, isDarkMode)
                        : isDarkMode
                        ? "bg-gray-800 border-gray-600 hover:bg-gray-700"
                        : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    {group} ({count})
                  </button>
                ))}
              </div>

              {/* Quick Selection by Position */}
              <div className={`p-2 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}>
                <p className="text-xs opacity-60 mb-2">Sélection rapide par poste :</p>
                <div className="flex flex-wrap gap-1">
                  {availablePositions.map(({ group, count }) => {
                    const selectedCount = selectedCountByPosition[group] || 0;
                    const allSelected = selectedCount === count;
                    return (
                      <button
                        key={group}
                        type="button"
                        onClick={() => allSelected ? handleDeselectByPosition(group) : handleSelectByPosition(group)}
                        className={`px-2 py-1 text-xs rounded border transition flex items-center gap-1 ${
                          allSelected
                            ? getPositionColor(group, isDarkMode)
                            : isDarkMode
                            ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                            : "bg-white border-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        {allSelected && <Check size={12} />}
                        {group}
                        <span className="opacity-60">({selectedCount}/{count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={handleSelectAll}
              className={`px-3 py-1.5 text-xs rounded transition ${
                isDarkMode
                  ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                  : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              }`}
            >
              <Check size={12} className="inline mr-1" />
              Sélectionner {selectedPositionFilter ? `les ${selectedPositionFilter}s` : "tout"}
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className={`px-3 py-1.5 text-xs rounded transition ${
                isDarkMode
                  ? "bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"
                  : "bg-rose-100 text-rose-700 hover:bg-rose-200"
              }`}
            >
              <X size={12} className="inline mr-1" />
              Désélectionner {selectedPositionFilter ? `les ${selectedPositionFilter}s` : "tout"}
            </button>
          </div>

          {/* Players List */}
          <div className="max-h-60 overflow-y-auto space-y-1">
            {filteredPlayers.length === 0 ? (
              <p className="text-center py-4 opacity-50 text-sm">
                {t("calendar_ui.no_player_found")}
              </p>
            ) : (
              filteredPlayers.map((p) => {
                const isSelected = assignedPlayers.includes(p.id);
                const positionGroup = getPositionGroup(p.position);
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${
                      isSelected
                        ? isDarkMode
                          ? "bg-sky-500/20 border border-sky-500/30"
                          : "bg-sky-100 border border-sky-200"
                        : isDarkMode
                        ? "hover:bg-gray-800 border border-transparent"
                        : "hover:bg-gray-100 border border-transparent"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTogglePlayer(p.id)}
                      className="w-4 h-4 rounded accent-sky-500"
                    />
                    {/* Jersey Number */}
                    {(p.number || p.jersey_number) && (
                      <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold rounded ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}>
                        {p.number || p.jersey_number}
                      </span>
                    )}
                    {/* Player Name */}
                    <span className="flex-1 text-sm truncate">{getPlayerName(p)}</span>
                    {/* Position Badge */}
                    {p.position && (
                      <span className={`px-1.5 py-0.5 text-[10px] rounded border ${getPositionColor(positionGroup, isDarkMode)}`}>
                        {p.position}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="mt-3 pt-2 border-t border-gray-600/30 flex items-center justify-between">
            <p className="text-xs opacity-60">
              {assignedPlayers.length} joueur{assignedPlayers.length > 1 ? "s" : ""} sélectionné{assignedPlayers.length > 1 ? "s" : ""}
            </p>
            {filteredPlayers.length !== players.length && (
              <p className="text-xs opacity-60">
                {filteredPlayers.length} affiché{filteredPlayers.length > 1 ? "s" : ""} sur {players.length}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerAssignmentSection;
